import { FC, ReactNode, memo, useMemo, useRef, useEffect, useState } from 'react';
import { cn, Select } from '@crisellevaldez/speculo-ui';
import { useOrgRecentActivitiesGQL } from '../../hooks/graphql/dashboard/useOrgRecentActivitiesGQL';
import { formatPhoneNumber } from '../../utils/formatters';
import { buildActivityDescription } from '../../utils/activity/buildActivityDescription';
import { filterReturnedAnsweredCallNotes } from '../../utils/activity/filterReturnedAnsweredCallNotes';
import { getActivityIcon, getActivityIconBg } from '../../utils/activity/activityHelpers';
import {
  ActivityInlineBadges,
  ActivityDescriptionBlock,
  buildWebBadge,
  buildEmailBadge,
} from '../common/ActivityContent';
import { LiveIndicator } from '../common/LiveIndicator';
import { CallDetailsModal, type CallRecord } from '../features/calls/CallDetailsModal';
import { SmsThreadModal, type SmsThreadAnchor } from '../features/dashboard/SmsThreadModal';
import { useFlywheelEnabled } from '../../stores/useOrganizationStore';

type ActivityFilter = 'call' | 'email' | 'sms' | 'web';

const FILTER_OPTIONS: { value: ActivityFilter; label: string }[] = [
  { value: 'call', label: 'Calls' },
  { value: 'email', label: 'Emails' },
  { value: 'sms', label: 'SMS' },
  { value: 'web', label: 'Website' },
];

const CHANNEL_BY_FILTER: Record<ActivityFilter, string> = {
  call: 'voice',
  email: 'email',
  sms: 'sms',
  web: 'web',
};

// event_type values the UI renders. Pushed to the server so the result set
// matches what we render — otherwise the IntersectionObserver sentinel can
// loop through pages that are mostly page_view/custom rows hidden client-side.
const VISIBLE_EVENT_TYPES = [
  'call',
  'email',
  'sms',
  'property_view',
  'search',
  'lead_capture',
  'property_saved',
  'valuation_request',
  'registration',
];

/**
 * Combine the selected filter chips into server-side `channels` args.
 *
 * Backend activity feed (UNION of Calls + Messages + WebsiteActivity + WebUsers)
 * stores these literal channel values:
 *   'voice'  → calls
 *   'email'  → email messages
 *   'sms'    → sms messages
 *   'web'    → web events + registrations
 *
 * Empty selection → no filter (server returns everything).
 */
const filtersToQueryArgs = (filters: ActivityFilter[]): { channels?: string[] } => {
  if (filters.length === 0) return {};
  return { channels: filters.map((f) => CHANNEL_BY_FILTER[f]) };
};

interface DisplayActivity {
  id: string;
  type: string;
  contactId?: string;
  contactName?: string;
  contactPhone?: string;
  contact: string;
  title: string;
  description: ReactNode;
  outcome?: string;
  timestamp: string;
  icon: ReactNode;
  iconBg: string;
  emailBodyHtml?: string;
  emailMeta?: {
    subject?: string;
    fromAddress?: string;
    toAddress?: string;
    templateName?: string;
    sentAt?: string;
    contactName?: string;
    contactEmail?: string;
  };
  webBadge?: { label: string; className: string };
  emailBadge?: { label: string; className: string };
  durationSeconds?: number;
  /** Kept so the row's call/SMS quick-action buttons can build the
   *  detail-modal payload without re-finding the raw item by id. */
  raw: RecentActivityItem;
  /** Parsed metadata JSON — same one used to derive title/icon/etc. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  meta: Record<string, any>;
}

/** Map a RecentActivityItem to the Activity shape needed by shared icon helpers */
function toActivityShape(
  item: RecentActivityItem,
  meta: Record<string, unknown>
): { type: string; sourceData?: Record<string, unknown> } {
  const type = item.eventType?.toLowerCase() ?? '';
  const isCall = type === 'call' || type.includes('call');
  const isSms = type === 'sms' || item.channel === 'sms';
  const isEmail = type === 'email' || item.channel === 'email';
  const activityType = isCall ? 'call' : isSms ? 'sms' : isEmail ? 'email' : 'website';

  return {
    type: activityType,
    sourceData: {
      metadata: item.metadata,
      event_type: item.eventType,
      // Direction drives the inbound/outbound glyph for calls + SMS.
      direction: item.direction || (meta.direction as string | undefined),
      event_subtype: item.eventSubtype,
      event_category: (meta.event_category || meta.eventCategory || '') as string,
      page_url: (meta.page_url || meta.pageUrl || '') as string,
    },
  };
}

function formatEventTitle(eventType: string, eventSubtype?: string): string {
  const combined = eventSubtype ? `${eventType} · ${eventSubtype}` : eventType;
  return combined.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function buildNarrativeAction(
  item: RecentActivityItem,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  meta: Record<string, any>,
): string {
  const type = item.eventType?.toLowerCase() ?? '';
  const channel = item.channel?.toLowerCase() ?? '';
  const subtype = item.eventSubtype?.toLowerCase() ?? '';
  // Some events carry direction at the top level; many calls (Bland's
  // ActivityEventSummary in particular) only set it inside the metadata
  // JSON. Resolve from both so inbound calls aren't mis-labelled as
  // "got a call from Remi" when the top-level field is null.
  const metaDirection =
    typeof meta?.direction === 'string' ? meta.direction.toLowerCase() : '';
  const directionRaw = item.direction?.toLowerCase() ?? metaDirection ?? '';

  if (type === 'call' || type.includes('call')) {
    const direction = directionRaw || subtype;
    return direction === 'inbound' || direction === 'received' || channel === 'inbound'
      ? 'called Remi'
      : 'got a call from Remi';
  }
  if (type === 'sms' || channel === 'sms') {
    const direction = directionRaw || subtype;
    return direction === 'inbound' || direction === 'received'
      ? 'replied via SMS'
      : 'got a text from Remi';
  }
  if (type === 'email' || channel === 'email') {
    // Use outcome (= message_status) for lifecycle; eventSubtype is direction.
    const status = item.outcome?.toLowerCase() ?? subtype;
    if (status.includes('click')) return 'clicked a link in an email';
    if (status.includes('open')) return 'opened an email';
    if (
      status.includes('bounce') ||
      status === 'failed' ||
      status === 'dropped' ||
      status === 'blocked'
    )
      return 'email failed to deliver';
    if (status === 'spamreport') return 'reported an email as spam';
    if (status === 'deferred') return 'email delivery deferred';
    if (status.includes('unsubscrib')) return 'unsubscribed from emails';
    if (status === 'delivered') return 'received an email';
    if (status === 'sent' || status === 'outbound' || status === 'queued' || status === 'processed')
      return 'was sent an email';
    if (subtype === 'inbound' || status === 'received' || status === 'inbound')
      return 'replied via email';
    return 'was sent an email';
  }
  if (type === 'property_saved') return 'saved a property';
  if (type.includes('property_view') || type.includes('property'))
    return 'viewed a property listing';
  if (type.includes('valuation') || type.includes('home_val')) return 'checked their home value';
  if (type.includes('search')) return 'searched for homes';
  if (type.includes('lead_capture') || type.includes('conversion')) return 'registered on the site';
  if (type.includes('page_view') || type.includes('web')) return 'browsed the website';
  return formatEventTitle(item.eventType, item.eventSubtype);
}

function formatTimestamp(eventAt: string): string {
  try {
    const normalized = /Z$|[+-]\d{2}:?\d{2}$/.test(eventAt) ? eventAt : eventAt + 'Z';
    const date = new Date(normalized);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs} hour${diffHrs > 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHrs / 24);
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

interface RecentActivityItem {
  eventId: string;
  eventType: string;
  channel?: string;
  direction?: string;
  contactId?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  eventSubtype?: string;
  summary?: string;
  outcome?: string;
  durationSeconds?: number;
  callId?: string;
  messageId?: string;
  metadata?: string;
  eventAt: string;
}

/**
 * Adapt a recent-activity call event into the CallRecord shape used by
 * the shared CallDetailsModal. Most fields come straight from the
 * activity feed; transcript / timeline / answers live inside the
 * metadata JSON, so we pull them out here.
 */
function buildCallRecord(
  item: RecentActivityItem,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  meta: Record<string, any>,
): CallRecord {
  const direction = (item.direction || meta?.direction || '').toString().toLowerCase();
  // Timeline / answers come down as JSON strings inside metadata. Parse
  // when possible, fall through to undefined so the panel renders the
  // "no analysis" empty state instead of crashing on bad JSON.
  const parseEmbedded = (v: unknown) => {
    if (!v || v === 'null') return undefined;
    if (typeof v === 'string') {
      try {
        const parsed = JSON.parse(v);
        return parsed === null ? undefined : parsed;
      } catch {
        return undefined;
      }
    }
    return v;
  };
  const timeline = parseEmbedded(meta?.call_timeline);
  const answers = parseEmbedded(meta?.call_answers);
  const callId = item.callId || item.eventId;
  return {
    id: callId,
    call_id: callId,
    callTimestamp: item.eventAt,
    inbound: direction === 'inbound',
    duration: item.durationSeconds ?? 0,
    corrected_duration: item.durationSeconds ?? 0,
    concatenated_transcript: typeof meta?.transcript === 'string' ? meta.transcript : undefined,
    recordingUrl:
      typeof meta?.recording_url === 'string'
        ? meta.recording_url
        : typeof meta?.recordingUrl === 'string'
          ? meta.recordingUrl
          : undefined,
    call_analysis: {
      outcome: item.outcome ?? '',
      summary: item.summary ?? undefined,
      timeline: Array.isArray(timeline) ? timeline : undefined,
      answers: answers && typeof answers === 'object' ? answers : undefined,
    },
  };
}

function looksLikePhone(str: string): boolean {
  const digits = str.replace(/\D/g, '');
  return digits.length >= 10 && /^[+\d\s().-]+$/.test(str);
}

function formatContactDisplay(item: RecentActivityItem): string {
  const name = item.contactName;
  if (name && !looksLikePhone(name)) return name;
  if (item.contactEmail) return item.contactEmail;
  const phone = name || item.contactPhone;
  if (phone) return formatPhoneNumber(phone);
  return 'Unknown';
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function parseMeta(item: RecentActivityItem): Record<string, any> {
  if (!item.metadata) return {};
  try {
    return JSON.parse(item.metadata);
  } catch {
    return {};
  }
}

function buildRichDescription(item: RecentActivityItem): ReactNode {
  return buildActivityDescription({
    eventType: item.eventType,
    eventSubtype: item.eventSubtype,
    channel: item.channel,
    summary: item.summary,
    outcome: item.outcome,
    durationSeconds: item.durationSeconds,
    metadata: item.metadata,
  });
}

function mapActivity(item: RecentActivityItem): DisplayActivity {
  const meta = parseMeta(item);
  const shape = toActivityShape(item, meta);
  const icon = getActivityIcon(shape as any, 'h-3.5 w-3.5'); // eslint-disable-line @typescript-eslint/no-explicit-any
  const iconBg = getActivityIconBg(shape);

  // Override property_view title when metadata indicates valuation
  let title = buildNarrativeAction(item, meta);
  if (item.eventType === 'property_view') {
    const eventCategory = meta.event_category || meta.eventCategory || '';
    const pageUrl = meta.page_url || meta.pageUrl || '';
    if (eventCategory.includes('Valuation') || pageUrl?.includes('valuation')) {
      title = 'checked their home value';
    }
  }

  // Build web event badge using shared utility
  const eventCategory = meta.event_category || meta.eventCategory || '';
  const pageUrl = meta.page_url || meta.pageUrl || '';
  const webBadge = buildWebBadge(item.eventType, eventCategory, pageUrl);
  const emailBadge = buildEmailBadge(item.eventType, item.outcome);

  return {
    id: item.eventId,
    type: item.eventType,
    contactId: item.contactId,
    contactName: item.contactName,
    contactPhone: item.contactPhone,
    contact: formatContactDisplay(item),
    title,
    description: buildRichDescription(item),
    outcome:
      item.eventType?.toLowerCase().includes('call') && item.outcome ? item.outcome : undefined,
    timestamp: formatTimestamp(item.eventAt),
    icon,
    iconBg,
    emailBodyHtml: meta.bodyHtml || undefined,
    emailMeta: {
      subject: meta.subject,
      fromAddress: meta.fromAddress || meta.from_address || meta.from,
      toAddress: meta.toAddress || meta.to_address || meta.to,
      templateName: meta.templateName || meta.template_name || meta.template,
      sentAt: meta.sentAt || meta.sent_at,
      contactName: item.contactName,
      contactEmail: item.contactEmail,
    },
    webBadge,
    emailBadge,
    durationSeconds:
      item.eventType === 'call' && item.durationSeconds && item.durationSeconds > 0
        ? item.durationSeconds
        : undefined,
    raw: item,
    meta,
  };
}

interface RecentActivitiesProps {
  onContactClick?: (contactId: string) => void;
  /** Extra header content rendered to the left of the filter Select.
   *  Lets the caller (e.g. LeadActivity) inject a shared sort dropdown. */
  headerActions?: React.ReactNode;
}

export const RecentActivities: FC<RecentActivitiesProps> = memo(
  ({ onContactClick, headerActions }) => {
    // Read the entitlement here rather than taking it as a prop: a prop needs a
    // default, and any default that isn't "hidden" leaks SMS to a new caller
    // that forgets to pass it.
    const flywheelEnabled = useFlywheelEnabled();
    const [filters, setFilters] = useState<ActivityFilter[]>([]);
    const queryArgs = useMemo(() => filtersToQueryArgs(filters), [filters]);
    // SMS is a flywheel-only channel — drop the SMS chip and exclude SMS rows
    // for non-flywheel orgs (mirrors the SMS dashboard widget gating).
    const filterOptions = flywheelEnabled
      ? FILTER_OPTIONS
      : FILTER_OPTIONS.filter((o) => o.value !== 'sms');
    const eventTypes = flywheelEnabled
      ? VISIBLE_EVENT_TYPES
      : VISIBLE_EVENT_TYPES.filter((t) => t !== 'sms');

    // Quick-action modals — whole-row click still opens the contact edit
    // modal via onContactClick. These per-row icon buttons (inside
    // ActivityInlineBadges) stop propagation and open a focused viewer.
    const [selectedCall, setSelectedCall] = useState<CallRecord | null>(null);
    const [selectedSmsContact, setSelectedSmsContact] = useState<{
      contactId: string;
      contactName?: string | null;
      contactPhone?: string | null;
      anchor?: SmsThreadAnchor | null;
    } | null>(null);

    const { activities, isLoading, isLoadingMore, hasMore, loadMore } = useOrgRecentActivitiesGQL({
      channels: queryArgs.channels,
      eventTypes,
    });

    const items = useMemo(
      () => filterReturnedAnsweredCallNotes(activities as RecentActivityItem[]).map(mapActivity),
      [activities]
    );

    const scrollRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const loadingMore = useRef(false);

    // IntersectionObserver on a sentinel at the bottom of the list. This is
    // more reliable than a scrollTop threshold (which can miss fast scrolls
    // and gets blocked when the global `isLoading` flips during background
    // refetches), and it avoids the layout-shift "shake" caused by triggering
    // mid-scroll based on stale scrollHeight.
    useEffect(() => {
      const sentinel = sentinelRef.current;
      const root = scrollRef.current;
      if (!sentinel || !root || !hasMore) return;

      const io = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry?.isIntersecting) return;
          if (loadingMore.current) return;
          loadingMore.current = true;
          loadMore().finally(() => {
            loadingMore.current = false;
          });
        },
        { root, rootMargin: '200px', threshold: 0 }
      );
      io.observe(sentinel);
      return () => io.disconnect();
    }, [hasMore, loadMore]);

    return (
      <div className="feed-card">
        <div className="feed-header">
          {/* Stacks on mobile so the filter dropdown doesn't get cramped
              against the title; inline from sm+ where there's room. */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3 w-full min-w-0">
            <div className="flex items-center gap-3 min-w-0">
              <h3 className="whitespace-nowrap">Recent Activities</h3>
              <div className="hidden sm:inline-flex">
                <LiveIndicator size="sm" showLabel />
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-5 sm:ml-auto flex-wrap">
              <div className="feed-filter shrink-0">
                <Select
                  multiple
                  variant="ghost"
                  value={filters}
                  onChange={(v) => setFilters((Array.isArray(v) ? v : [v]) as ActivityFilter[])}
                  options={filterOptions}
                  placeholder="All Filter"
                />
              </div>
              {headerActions}
            </div>
          </div>
        </div>

        <div className="feed-body scrollbar-custom" ref={scrollRef}>
          {isLoading &&
            items.length === 0 &&
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="feed-row animate-pulse">
                <div className="mr-2.5 md:mr-3 mt-0.5">
                  <div className="rounded-full p-1.5 md:p-2 bg-muted w-7 h-7 md:w-8 md:h-8" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="h-3.5 w-28 rounded bg-muted" />
                    <div className="h-3 w-10 rounded bg-muted shrink-0 mt-px" />
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <div className="h-3 w-20 rounded bg-muted" />
                  </div>
                </div>
              </div>
            ))}
          {!isLoading && items.length === 0 && (
            <div className="flex flex-1 items-center justify-center text-muted-foreground text-sm">
              No recent activities
            </div>
          )}
          {items.map((activity, _idx) => {
            const isClickable = !!(onContactClick && activity.contactId);
            const hasDescription = Boolean(activity.description);
            const rowContent = (
              <>
                <div
                  className={`${activity.iconBg} rounded-full p-1.5 md:p-2 mr-2.5 md:mr-3 ${hasDescription ? 'mt-0.5' : ''} shrink-0`}
                >
                  {activity.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
                      {/* On hover the row resolves to "open contact" —
                          underlining + primaryfortext tint makes the
                          contact name read as the click target, so the
                          per-channel hover buttons (Open thread / View
                          call / Preview email) stay clearly the
                          alternative action. */}
                      <span className="font-semibold text-xs sm:text-sm text-foreground group-hover:text-primaryfortext group-hover:underline transition-colors">
                        {activity.contact}
                      </span>
                      <span className="text-xs sm:text-sm text-muted-foreground">
                        {activity.title}
                      </span>
                      <ActivityInlineBadges
                        eventType={activity.type}
                        durationSeconds={activity.durationSeconds}
                        outcome={activity.outcome}
                        emailBodyHtml={activity.emailBodyHtml}
                        emailMeta={activity.emailMeta}
                        webBadge={activity.webBadge}
                        emailBadge={activity.emailBadge}
                        onViewCall={
                          activity.raw.callId
                            ? () => setSelectedCall(buildCallRecord(activity.raw, activity.meta))
                            : undefined
                        }
                        onViewSms={
                          activity.contactId
                            ? () => {
                                // Build the anchor from the activity row so the
                                // thread modal can render the bubble locally
                                // (backend's before/after cursors are exclusive
                                // of the anchor itself — see PR #2423). meta is
                                // the parsed metadata JSON; backend puts the SMS
                                // body / direction there for SMS rows.
                                const messageId = activity.raw.messageId;
                                const anchor: SmsThreadAnchor | null = messageId
                                  ? {
                                      messageId,
                                      body:
                                        (activity.meta?.body as string | undefined) ??
                                        activity.raw.summary ??
                                        null,
                                      direction:
                                        (activity.meta?.direction as string | undefined) ??
                                        activity.raw.direction ??
                                        null,
                                      sentAt:
                                        (activity.meta?.sent_at as string | undefined) ??
                                        (activity.meta?.sentAt as string | undefined) ??
                                        activity.raw.eventAt ??
                                        null,
                                      createdAt: activity.raw.eventAt ?? null,
                                    }
                                  : null;
                                setSelectedSmsContact({
                                  contactId: activity.contactId!,
                                  contactName: activity.contactName ?? null,
                                  contactPhone: activity.contactPhone ?? null,
                                  anchor,
                                });
                              }
                            : undefined
                        }
                      />
                    </div>
                    {/* Inline timestamp on sm+ — moved below on mobile so it doesn't squish the row */}
                    <span className="hidden sm:inline text-[10px] xl:text-xs text-muted-foreground uppercase whitespace-nowrap shrink-0">
                      {activity.timestamp}
                    </span>
                  </div>
                  {activity.description && (
                    <div className="hidden lg:block">
                      <ActivityDescriptionBlock description={activity.description} />
                    </div>
                  )}
                  {/* Mobile-only timestamp on its own line */}
                  <span className="sm:hidden block mt-1 text-[10px] text-muted-foreground uppercase">
                    {activity.timestamp}
                  </span>
                </div>
              </>
            );

            const alignClass = hasDescription ? '' : '!items-center';
            return isClickable ? (
              <button
                key={`${activity.id}-${_idx}`}
                onClick={() => onContactClick!(activity.contactId!)}
                className={cn(
                  'group feed-row opacity-100 translate-y-0 w-full text-left hover:bg-muted/50 transition-colors',
                  alignClass
                )}
              >
                {rowContent}
              </button>
            ) : (
              <div
                key={`${activity.id}-${_idx}`}
                className={cn('group feed-row', 'opacity-100 translate-y-0', alignClass)}
              >
                {rowContent}
              </div>
            );
          })}
          {isLoadingMore && (
            <div className="flex justify-center py-3">
              <div className="h-4 w-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
            </div>
          )}
          {/* IntersectionObserver target — triggers loadMore well before the
            user actually reaches the bottom (rootMargin 200px) so they never
            see an empty gap that would cause the section to "shake". */}
          {hasMore && <div ref={sentinelRef} aria-hidden className="h-1 w-full" />}
        </div>
        <CallDetailsModal
          isOpen={!!selectedCall}
          onClose={() => setSelectedCall(null)}
          call={selectedCall}
        />
        <SmsThreadModal
          isOpen={!!selectedSmsContact}
          onClose={() => setSelectedSmsContact(null)}
          contactId={selectedSmsContact?.contactId ?? null}
          contactName={selectedSmsContact?.contactName ?? null}
          contactPhone={selectedSmsContact?.contactPhone ?? null}
          anchor={selectedSmsContact?.anchor ?? null}
          onContactClick={onContactClick}
        />
      </div>
    );
  }
);
